import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Trophy, BookOpen, Store as StoreIcon } from "lucide-react";
import CoverThumb from "@/components/CoverThumb";

const RANGES = [
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "12 months", days: 365 },
];

function Row({ index, name, units, subtitle, thumb }) {
  return (
    <div className="flex items-center gap-3 py-2.5">
      <div className="w-5 text-right font-mono text-xs text-muted-foreground">{index + 1}</div>
      {thumb}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{name}</div>
        {subtitle && <div className="text-xs text-muted-foreground truncate">{subtitle}</div>}
      </div>
      <div className="text-right shrink-0">
        <div className="font-display text-lg leading-tight">{units.toLocaleString()}</div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">sold</div>
      </div>
    </div>
  );
}

export default function LeaderboardCard() {
  const { user } = useAuth();
  const [range, setRange] = useState(30);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("titles");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/reports/leaderboard", { params: { days: range, limit: 10 } });
        if (!cancel) setData(data);
      } catch (e) { console.warn("leaderboard load failed", e); if (!cancel) setData({ top_titles: [], top_stores: [] }); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [range]);

  const showStoresTab = user.role === "admin";
  const titlesRows = useMemo(
    () => (data?.top_titles || []).map((t) => ({ ...t, subtitle: [t.sku, t.author].filter(Boolean).join(" · ") })),
    [data],
  );

  return (
    <div className="grid-border-card overflow-hidden" data-testid="leaderboard-card">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-accent" strokeWidth={1.5}/>
          <div>
            <div className="overline">Leaderboard</div>
            <div className="font-display text-xl mt-0.5">Top sellers</div>
          </div>
        </div>
        <div className="flex gap-1 text-xs font-mono">
          {RANGES.map(r => (
            <button
              key={r.days}
              type="button"
              onClick={() => setRange(r.days)}
              className={`px-2.5 py-1 rounded-sm border transition-colors ${range === r.days ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground/50"}`}
              data-testid={`leaderboard-range-${r.days}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {showStoresTab && (
        <div className="px-6 pt-3 flex gap-4 border-b border-border">
          <button
            type="button"
            onClick={() => setTab("titles")}
            className={`text-xs uppercase tracking-widest pb-2 -mb-px border-b-2 ${tab === "titles" ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid="leaderboard-tab-titles"
          >
            <BookOpen className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5"/>Books
          </button>
          <button
            type="button"
            onClick={() => setTab("stores")}
            className={`text-xs uppercase tracking-widest pb-2 -mb-px border-b-2 ${tab === "stores" ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid="leaderboard-tab-stores"
          >
            <StoreIcon className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5"/>Stores
          </button>
        </div>
      )}

      <div className="px-6 py-2 divide-y divide-border">
        {loading && <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>}

        {!loading && tab === "titles" && titlesRows.map((t, i) => (
          <Row
            key={t.product_id}
            index={i}
            name={t.name}
            subtitle={t.subtitle}
            units={t.units}
            thumb={<CoverThumb productId={t.product_id} coverPath={t.cover_path} size={40}/>}
          />
        ))}

        {!loading && tab === "stores" && (data?.top_stores || []).map((s, i) => (
          <Row
            key={s.store_id}
            index={i}
            name={s.name}
            subtitle={s.code}
            units={s.units}
            thumb={
              <div className="w-10 h-10 rounded-sm bg-secondary flex items-center justify-center shrink-0">
                <StoreIcon className="w-4 h-4 text-muted-foreground"/>
              </div>
            }
          />
        ))}

        {!loading && (tab === "titles" ? data?.top_titles?.length : data?.top_stores?.length) === 0 && (
          <div className="text-sm text-muted-foreground py-8 text-center">
            No sales recorded in this range yet.
          </div>
        )}
      </div>
    </div>
  );
}
